import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { EmailProvider, EmailSetting, newEmailProvider, newEmailSetting } from "../Email";
import { SelectServerSearch, SingleSelectSearch } from "../../components/ui/DropdownSelect";

interface EmailSettingFormProps {
  item: EmailSetting;
  error?: string;
  EmailProviderList?: EmailProvider[];
}

export interface EmailSettingFormRef {
  valid: () => boolean;
  getItem: () => EmailSetting;
}

export const EmailSettingForm = forwardRef((props: EmailSettingFormProps, ref) => {
  const { error, EmailProviderList } = props;
  const [item, setItem] = useState<EmailSetting>(newEmailSetting());
  const [submitted, setSubmitted] = useState(false);
  const [providerList, setProviderList] = useState<EmailProvider[]>([]);

  useEffect(() => {
    setItem(props.item);
    if(props.EmailProviderList){
      setProviderList(props.EmailProviderList)
    }
  }, [props.item]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(item) as (keyof EmailSetting)[]) {
      if (typeof item[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getItem = () => {
    return item;
  }

  // Expose the valid function to the parent, and a method to get the whole item object
  useImperativeHandle(ref, () => ({
    valid,
    getItem,
  }));

  const validateBaseField = (field: keyof EmailSetting): string => {
    if (field === "name") {
      if (!item.name?.trim()) return "Navn kan ikke være tom";
    }
    return ""; // No error
  };

  const handleBaseChange = (field: keyof EmailSetting, value: any) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleBaseBlur = (field: keyof EmailSetting) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12}>
          <TextField
            label="Navn"
            required
            fullWidth
            value={item.name}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Brukernavn"
            fullWidth
            value={item.username}
            onChange={(e) => handleBaseChange("username", e.target.value)}
            variant="outlined"
            error={validateBaseField("username") !== "" && (submitted || touched.base.username)}
            helperText={(submitted || touched.base.username) ? validateBaseField("username") : ""}
            onBlur={() => handleBaseBlur("username")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Passord"
            fullWidth
            value={item.password}
            onChange={(e) => handleBaseChange("password", e.target.value)}
            variant="outlined"
            error={validateBaseField("password") !== "" && (submitted || touched.base.password)}
            helperText={(submitted || touched.base.password) ? validateBaseField("password") : ""}
            onBlur={() => handleBaseBlur("password")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SingleSelectSearch
            label="E-postleverandør"
            inputs={providerList  || []}
            selectField="name"
            selected={item.sys_email_provider}
            onChange={(id) => {
              const selectedSolution = providerList.find((ps) => ps.id === id);
              if (selectedSolution) {
                setItem((prev) => ({
                  ...prev,
                  sys_email_provider: selectedSolution.id,
                }));
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={!!item.auth_by_user}
                onChange={(e) => handleBaseChange("auth_by_user", e.target.checked)}
              />
            }
            label="Brukerautorisering"
          />
        </Grid>
      </Grid>
    </form>
  )
});